import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from './firebase';
import { isDeleted } from './trash';
import { formatCaseTitle } from './utils';
import { draftsListCacheKey, setCached } from './listCache';

export interface DraftListRecord {
  id: string;
  title?: string;
  content?: string;
  type: string;
  documentType?: string;
  workflowStatus?: string;
  updatedAt?: string;
  createdAt?: string;
  caseId?: string;
  lawyerId: string;
  workspaceId: string;
  templateCaseTypes?: string[];
  templateSides?: string[];
  templateStagesByCaseType?: Record<string, string[]>;
}

export interface DraftsListCacheData {
  drafts: DraftListRecord[];
  templates: DraftListRecord[];
  casesMap: Record<string, string>;
  casesData: Record<string, unknown>[];
  casesById: Record<string, Record<string, unknown>>;
}

function sortByRecency(a: DraftListRecord, b: DraftListRecord): number {
  const timeA = a.updatedAt
    ? (a.updatedAt as { toMillis?: () => number })?.toMillis?.() ||
      new Date(a.updatedAt).getTime()
    : a.createdAt
      ? (a.createdAt as { toMillis?: () => number })?.toMillis?.() ||
        new Date(a.createdAt).getTime()
      : 0;
  const timeB = b.updatedAt
    ? (b.updatedAt as { toMillis?: () => number })?.toMillis?.() ||
      new Date(b.updatedAt).getTime()
    : b.createdAt
      ? (b.createdAt as { toMillis?: () => number })?.toMillis?.() ||
        new Date(b.createdAt).getTime()
      : 0;
  return timeB - timeA;
}

export async function fetchDraftsList(
  workspaceId: string,
): Promise<DraftsListCacheData> {
  const ws = workspaceId || 'no-ws';
  const docsRef = collection(db, 'workspaces', ws, 'documents');
  const qDocs = query(
    docsRef,
    where('type', 'in', ['draft', 'template']),
    orderBy('updatedAt', 'desc'),
    limit(50),
  );

  const docSnap = await getDocs(qDocs);

  const fetchedDrafts: DraftListRecord[] = [];
  const fetchedTemplates: DraftListRecord[] = [];

  docSnap.forEach((d) => {
    const data = { id: d.id, ...d.data() } as DraftListRecord;
    const raw = d.data() as Record<string, unknown>;
    if (isDeleted(raw)) return;
    if (data.type === 'draft') {
      if (raw.source === 'uploads' || raw.source === 'old_case_import') return;
      fetchedDrafts.push(data);
    } else if (data.type === 'template') {
      fetchedTemplates.push(data);
    }
  });

  fetchedDrafts.sort(sortByRecency);
  fetchedTemplates.sort(sortByRecency);

  const caseIds = [
    ...new Set(
      fetchedDrafts.map((d) => d.caseId).filter((id): id is string => Boolean(id)),
    ),
  ];

  const cMap: Record<string, string> = {};
  const cData: Record<string, unknown>[] = [];
  const byId: Record<string, Record<string, unknown>> = {};

  if (caseIds.length > 0) {
    const casesRef = collection(db, 'workspaces', ws, 'cases');
    const caseSnaps = await Promise.all(
      caseIds.map((id) => getDoc(doc(casesRef, id))),
    );
    caseSnaps.forEach((snap) => {
      if (!snap.exists()) return;
      const data = { id: snap.id, ...snap.data() } as Record<string, unknown> & {
        id: string;
      };
      byId[snap.id] = data;
      cMap[snap.id] = formatCaseTitle((data.title as string) || '') || 'Unknown Case';
      cData.push(data);
    });
  }

  const result: DraftsListCacheData = {
    drafts: fetchedDrafts,
    templates: fetchedTemplates,
    casesMap: cMap,
    casesData: cData,
    casesById: byId,
  };

  setCached(draftsListCacheKey(workspaceId), result);
  return result;
}

export async function fetchDraftsListSafe(
  workspaceId: string,
): Promise<DraftsListCacheData | null> {
  try {
    return await fetchDraftsList(workspaceId);
  } catch (err) {
    handleFirestoreError(err, OperationType.GET, 'documents');
    return null;
  }
}
